import { EventEmitter, Input, OnChanges, SimpleChanges } from '@angular/core';
import { Component, OnInit } from '@angular/core';
import { PriceFormatPipe, PriceType } from '@app/shared/pipes/price-format.pipe';
import { VoiceAllowedChangeInfo } from '@app/sales/modules/subscription/models/offerings-response.model';
import { VoiceProduct } from '@app/shared/models/voice-product.model';
import { Output } from '@angular/core';
import { getAgeLimitStatus } from '@app/sales/validators/age.validator';
import { SubscriptionHelperService } from '@app/sales/services/subscription-helper.service';
import { PcmProductCategoryType } from '@app/sales/modules/subscription/models/pcm-product.model';
import { EquipmentPaymentType } from '@app/sales/enums/equipment-payment-type';
import { MobilePaymentType } from '@app/sales/enums/mobile-payment-type';
import { MatTooltip } from '@angular/material/tooltip';
import { MatRadioButton } from '@angular/material/radio';
import { IceBadgeComponent } from '@app/shared/components/atoms/ice-badge/ice-badge.component';
import { DatePipe } from '@angular/common';
import { SpinnerComponent } from '@app/shared/components/atoms/spinner/spinner.component';

@Component({
  selector: 'app-existing-offering-upgrade',
  imports: [MatRadioButton, MatTooltip, IceBadgeComponent, PriceFormatPipe, DatePipe, SpinnerComponent],
  templateUrl: './existing-offering-upgrade.component.html',
  styleUrls: ['./existing-offering-upgrade.component.scss'],
})
export class ExistingOfferingUpgradeComponent implements OnInit, OnChanges {
  @Input() voiceProduct: VoiceProduct;
  @Input() newOfferingId: string;
  @Input() subscriptionToUpgrade: string;
  @Input() equipmentPaymentType: EquipmentPaymentType | MobilePaymentType;
  @Input() existingProductsAvailableForEquipmentUpgrade: VoiceProduct[];
  @Output() resetSubscriptionIdToUpgrade = new EventEmitter();

  priceType = PriceType;
  SubscriptionHelperService = SubscriptionHelperService;
  changeInfoForUpgrade: VoiceAllowedChangeInfo;
  isUpgradeAllowed: boolean;
  subscriberExceedsUpperAgeLimit: boolean;
  subscriberIsBelowLowerAgeLimit: boolean;
  tooltipText: string = '';

  get displayTerminationFee(): boolean {
    const { triggersBreakageFee, remainingBreakageFee } = this.changeInfoForUpgrade?.changeInfo || {};
    const expirationDate = this.voiceProduct.subscription?.commitmentPeriods?.[0]?.expirationDate;

    return (
      !!this.isUpgradeAllowed &&
      !this.subscriberExceedsUpperAgeLimit &&
      !this.subscriberIsBelowLowerAgeLimit &&
      triggersBreakageFee &&
      remainingBreakageFee?.includingVAT > 0 &&
      new Date(expirationDate)?.getTime() > new Date().getTime()
    );
  }

  get vasOfferingProductName(): string {
    const vasAddon = this.voiceProduct.assignedSubscription?.addons.find(
      addon => addon.productDetails.pcmProductCategory?.value === PcmProductCategoryType.ValueAddedService
    );
    return !!vasAddon ? `(${vasAddon.productDetails.name})` : '';
  }

  ngOnInit() {
    this.changeInfoForUpgrade = this.voiceProduct.allowedSubscriptionOfferings?.find(
      x => x.subscriptionOffering.id === this.newOfferingId
    );
    this.isUpgradeAllowed =
      !!this.changeInfoForUpgrade &&
      (this.equipmentPaymentType !== EquipmentPaymentType.EquipmentUpgrade ||
        this.existingProductsAvailableForEquipmentUpgrade.some(x => x.id === this.voiceProduct.id));
  }
  ngOnChanges(changes: SimpleChanges) {
    if (changes.voiceProduct || changes.newOfferingId) {
      this.changeInfoForUpgrade = this.voiceProduct.allowedSubscriptionOfferings?.find(
        x => x.subscriptionOffering.id === this.newOfferingId
      );
      const { upperAgeLimit, lowerAgeLimit, name } = this.changeInfoForUpgrade?.subscriptionOffering || {};
      const { exceedsUpperAgeLimit, isBelowLowerAgeLimit } = getAgeLimitStatus(
        this.voiceProduct?.subscriber?.birthday,
        this.changeInfoForUpgrade?.subscriptionOffering
      );
      this.subscriberExceedsUpperAgeLimit = exceedsUpperAgeLimit;
      this.subscriberIsBelowLowerAgeLimit = isBelowLowerAgeLimit;

      this.isUpgradeAllowed =
        !!this.changeInfoForUpgrade &&
        (this.equipmentPaymentType !== EquipmentPaymentType.EquipmentUpgrade ||
          this.existingProductsAvailableForEquipmentUpgrade.some(x => x.id === this.voiceProduct.id));

      if (this.subscriberExceedsUpperAgeLimit || this.subscriberIsBelowLowerAgeLimit) {
        const isAbove = this.subscriberExceedsUpperAgeLimit;
        const ageLimit = isAbove ? upperAgeLimit : lowerAgeLimit;
        const comparison = isAbove ? 'over' : 'under';

        this.tooltipText = `Bruker er ${comparison} ${ageLimit} år og kan ikke kjøpe ${name}, endre bruker først for å velge abonnement.`;
      } else if (!this.isUpgradeAllowed) {
        this.tooltipText = 'Abonnement ikke tilgjengelig';
      } else {
        this.tooltipText = '';
      }

      const isVoiceSubscriptionNotAllowedToSelect =
        !this.isUpgradeAllowed || this.subscriberExceedsUpperAgeLimit || this.subscriberIsBelowLowerAgeLimit;
      const isVoiceSubscriptionAlreadySelected = this.voiceProduct.id === this.subscriptionToUpgrade;

      if (isVoiceSubscriptionNotAllowedToSelect && isVoiceSubscriptionAlreadySelected) {
        this.resetSubscriptionIdToUpgrade.emit();
      }
    }
  }
}
